import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApiClient } from '../../../lib/api/apiClientContext';
import { previewRequest } from '../../../lib/api/previewRequest';

export interface StudyItem {
  readonly id: string;
  readonly code: string;
  readonly protocolCode: string;
  readonly title: string;
  readonly phase: string;
  readonly therapeuticArea: string;
  readonly sponsorName: string;
  readonly croName: string | null;
  readonly status: string;
  readonly targetEnrollment: number;
  readonly currentEnrollment: number;
}

export interface OrganizationItem {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly kind: string;
  readonly country: string;
  readonly contactEmail: string;
  readonly status: string;
}

export interface ProcedureRegionalPriceBreakdownItem {
  readonly cityUf: string;
  readonly unitPriceBrl: number;
  readonly unitMeasure: string;
  readonly version: number;
}

export interface ProcedureDefinitionItem {
  readonly id: string;
  readonly tenantId: string | null;
  readonly code: string;
  readonly namePtBr: string;
  readonly nameEnUs: string;
  readonly category: string;
  readonly defaultDurationMinutes: number;
  readonly baseUnitCostBrl: number;
  readonly currentVersion: number;
  readonly status: string;
  readonly regionCityUf?: string | undefined;
  readonly attendingCenterName?: string | undefined;
  readonly supportedRegionsCityUf?: readonly string[] | undefined;
  readonly regionalPrices?: readonly ProcedureRegionalPriceBreakdownItem[] | undefined;
}

export interface StudyContractDossierItem {
  readonly id: string;
  readonly code: string;
  readonly studyCode: string;
  readonly title: string;
  readonly sponsorName: string;
  readonly croName: string | null;
  readonly currency: string;
  readonly exchangeRateBrl: number;
  readonly taxRegime: string;
  readonly taxRatePercent: number;
  readonly overheadPercent: number;
  readonly totalBaselineBrl: number;
  readonly totalNegotiatedBrl: number;
  readonly passThroughTotalBrl: number;
  readonly estimatedMarginPercent: number;
  readonly status: string;
  readonly riskScore: number;
  readonly clinicalAnalysisJson: string;
  readonly budgetGridJson: string;
  readonly contractClausesJson: string;
  readonly miroFishSimulationJson: string;
  readonly jevScoreJson: string;
  readonly linkedEmailsCount: number;
}

export interface SharedMailboxMessageItem {
  readonly id: string;
  readonly graphMessageId: string;
  readonly mailboxAddress: string;
  readonly mailboxFolder?: 'INBOX' | 'OUTBOX' | undefined;
  readonly subject: string;
  readonly senderName: string;
  readonly senderEmail: string;
  readonly senderDomain: string;
  readonly recipientEmailsJson?: string | undefined;
  readonly ccEmailsJson?: string | undefined;
  readonly conversationId?: string | undefined;
  readonly receivedAtIso: string;
  readonly bodyPreview: string;
  readonly hasAttachments: boolean;
  readonly attachmentNamesJson: string;
  readonly classificationStatus: 'AutoAssigned' | 'ManualAssigned' | 'Unassigned' | 'NotAProject';
  readonly assignedProjectCode: string | null;
  readonly confidenceScore: number;
  readonly matchedRulesJson: string;
  readonly manuallyReviewedBy: string | null;
}

export interface ThreadRelayHopEntryDto {
  readonly hopNumber: number;
  readonly actionType:
    'ORIGIN_SENT' | 'ATTACHMENT_INTRODUCED' | 'REPLIED' | 'FORWARDED_RELAY' | 'SENT_OUTBOX';
  readonly actionBadgeLabel: string;
  readonly timestampLabel: string;
  readonly fromActor: string;
  readonly toRecipients: string;
  readonly ccRecipients: string | null;
  readonly summaryText: string;
  readonly newlyEmergedAttachments: readonly string[];
  readonly repassedAttachments: readonly string[];
}

export interface ThreadAttachmentEmergenceEventDto {
  readonly fileName: string;
  readonly emergedAtHopOrStepLabel: string;
  readonly emergedAtTimestampLabel: string;
  readonly introducedByActor: string;
  readonly relayHistorySummary: string;
}

export interface SharedMailboxThreadTimelineEntryDto {
  readonly messageId: string;
  readonly graphMessageId: string;
  readonly stepNumber: number;
  readonly mailboxAddress: string;
  readonly mailboxFolder: 'INBOX' | 'OUTBOX';
  readonly directionLabel: string;
  readonly transmissionKind?: 'ORIGINAL_SENT' | 'FORWARDED_RELAY' | 'REPLY_INBOX' | 'SENT_OUTBOX';
  readonly transmissionBadgeLabel?: string;
  readonly subject: string;
  readonly senderName: string;
  readonly senderEmail: string;
  readonly recipientEmails: readonly string[];
  readonly ccEmails: readonly string[];
  readonly timestampIso: string;
  readonly timeDeltaFromPreviousLabel: string;
  readonly bodyPreview: string;
  readonly hasAttachments: boolean;
  readonly attachmentNames: readonly string[];
  readonly newlyEmergedAttachments?: readonly string[];
  readonly repassedAttachments?: readonly string[];
  readonly internalRelayHops?: readonly ThreadRelayHopEntryDto[];
  readonly classificationStatus: 'AutoAssigned' | 'ManualAssigned' | 'Unassigned' | 'NotAProject';
  readonly assignedProjectCode: string | null;
  readonly confidenceScore: number;
}

export interface SharedMailboxThreadDoubtItemDto {
  readonly id: string;
  readonly candidateThreadAId: string;
  readonly candidateThreadASubject: string;
  readonly candidateThreadBId: string;
  readonly candidateThreadBSubject: string;
  readonly messageIdsA: readonly string[];
  readonly messageIdsB: readonly string[];
  readonly senderSummaryA: string;
  readonly senderSummaryB: string;
  readonly receivedAtA: string;
  readonly receivedAtB: string;
  readonly similarityScorePercent: number;
  readonly doubtReason: string;
  readonly sharedSignals: readonly string[];
  readonly suggestedStudyCode: string | null;
  readonly status: 'PENDING_USER_CONFIRMATION' | 'CONFIRMED_MERGED' | 'REJECTED_SEPARATED';
  readonly resolvedByEmail: string | null;
  readonly resolvedAtIso: string | null;
}

export interface SharedMailboxThreadItemDto {
  readonly threadId: string;
  readonly canonicalSubject: string;
  readonly subjectVariants: readonly string[];
  readonly studyCode: string | null;
  readonly mailboxAddresses: readonly string[];
  readonly participants: readonly string[];
  readonly totalMessagesCount: number;
  readonly inboxMessagesCount: number;
  readonly outboxMessagesCount: number;
  readonly startedAtIso: string;
  readonly lastActivityAtIso: string;
  readonly groupingOrigin: 'AUTO_EXACT_SUBJECT' | 'USER_CONFIRMED_DOUBT' | 'USER_MANUAL_MERGE';
  readonly hasPendingDoubt: boolean;
  readonly attachmentEmergenceEvents?: readonly ThreadAttachmentEmergenceEventDto[];
  readonly timeline: readonly SharedMailboxThreadTimelineEntryDto[];
}

export interface SharedMailboxThreadsDashboardDto {
  readonly totalMessagesCount: number;
  readonly inboxMessagesCount: number;
  readonly outboxMessagesCount: number;
  readonly totalThreadsCount: number;
  readonly pendingDoubtsCount: number;
  readonly resolvedDoubtsCount: number;
  readonly threads: readonly SharedMailboxThreadItemDto[];
  readonly pendingDoubts: readonly SharedMailboxThreadDoubtItemDto[];
  readonly resolvedDoubts: readonly SharedMailboxThreadDoubtItemDto[];
}

export interface ClassificationRuleItem {
  readonly id: string;
  readonly projectCode: string;
  readonly projectName: string;
  readonly ruleKind: string;
  readonly pattern: string;
  readonly weight: number;
  readonly learnedFromHumanFeedback: boolean;
  readonly hitCount: number;
  readonly createdByRole: string;
}

export interface MailboxFeedbackActionResponse {
  readonly message: SharedMailboxMessageItem;
  readonly autoFittedPendingCount: number;
  readonly learnedRulesCount: number;
  readonly learnedRulePattern?: string | undefined;
  readonly dossierCreatedOrUpdatedCode?: string | null | undefined;
}

export interface CreateContractDossierResultResponse {
  readonly dossier: StudyContractDossierItem;
  readonly autoFittedEmailsCount: number;
  readonly learnedRulesCount: number;
}

export interface MailboxSyncResultResponse {
  readonly totalMessages: number;
  readonly inboxMessagesCount?: number | undefined;
  readonly outboxMessagesCount?: number | undefined;
  readonly groupedThreadsCount?: number | undefined;
  readonly pendingThreadDoubtsCount?: number | undefined;
  readonly autoAssignedCount: number;
  readonly manualAssignedCount: number;
  readonly unassignedCount: number;
  readonly notAProjectCount: number;
  readonly newlyDownloadedCount: number;
  readonly autoFittedCount: number;
  readonly syncedAtIso: string;
}

export function useStudies() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies'],
    queryFn: () => previewRequest<readonly StudyItem[]>(client, 'get', '/api/v1/network/studies'),
    meta: { silent: true },
  });
}

export function useOrganizations() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'organizations'],
    queryFn: () =>
      previewRequest<readonly OrganizationItem[]>(
        client,
        'get',
        '/api/v1/network/studies/organizations',
      ),
    meta: { silent: true },
  });
}

export function useProcedures() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'procedures'],
    queryFn: () =>
      previewRequest<readonly ProcedureDefinitionItem[]>(
        client,
        'get',
        '/api/v1/network/studies/procedures',
      ),
    meta: { silent: true },
  });
}

export function useContractDossiers() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'dossiers'],
    queryFn: () =>
      previewRequest<readonly StudyContractDossierItem[]>(
        client,
        'get',
        '/api/v1/network/studies/dossiers',
      ),
    meta: { silent: true },
  });
}

export function useMailboxMessages() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'mailboxes', 'messages'],
    queryFn: () =>
      previewRequest<readonly SharedMailboxMessageItem[]>(
        client,
        'get',
        '/api/v1/network/studies/mailboxes/messages',
      ),
    meta: { silent: true },
  });
}

export function useSharedMailboxThreads() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'mailboxes', 'threads'],
    queryFn: () =>
      previewRequest<SharedMailboxThreadsDashboardDto>(
        client,
        'get',
        '/api/v1/network/studies/mailboxes/threads',
      ),
    meta: { silent: true },
  });
}

export interface ResolveMailboxThreadDoubtInput {
  readonly doubtId: string;
  readonly decision: 'CONFIRM_SAME_THREAD' | 'REJECT_SEPARATE_THREADS';
  readonly reviewedBy: string;
}

export function useResolveMailboxThreadDoubt() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: ResolveMailboxThreadDoubtInput) =>
      previewRequest<SharedMailboxThreadsDashboardDto>(
        client,
        'post',
        '/api/v1/network/studies/mailboxes/threads/resolve-doubt',
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface ManualMergeMailboxThreadsInput {
  readonly sourceThreadId: string;
  readonly targetThreadId: string;
  readonly reviewedBy: string;
}

export function useManualMergeMailboxThreads() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: ManualMergeMailboxThreadsInput) =>
      previewRequest<SharedMailboxThreadsDashboardDto>(
        client,
        'post',
        '/api/v1/network/studies/mailboxes/threads/manual-merge',
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useClassificationRules() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'mailboxes', 'rules'],
    queryFn: () =>
      previewRequest<readonly ClassificationRuleItem[]>(
        client,
        'get',
        '/api/v1/network/studies/mailboxes/rules',
      ),
    meta: { silent: true },
  });
}

function useInvalidateStudiesAndMailboxes() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['network', 'studies'] });
  };
}

export function useSyncMailboxes() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: () =>
      previewRequest<MailboxSyncResultResponse>(
        client,
        'post',
        '/api/v1/network/studies/mailboxes/sync',
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface AssignMessageInput {
  readonly id: string;
  readonly projectCode: string;
  readonly reviewedBy: string;
  readonly customRuleToken?: string | undefined;
}

export function useAssignMailboxMessage() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: AssignMessageInput) =>
      previewRequest<MailboxFeedbackActionResponse>(
        client,
        'post',
        `/api/v1/network/studies/mailboxes/messages/${input.id}/assign`,
        {
          body: {
            projectCode: input.projectCode,
            reviewedBy: input.reviewedBy,
            ...(input.customRuleToken ? { customRuleToken: input.customRuleToken } : {}),
          },
        },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface UnassignMessageInput {
  readonly id: string;
  readonly reviewedBy: string;
}

export function useUnassignMailboxMessage() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: UnassignMessageInput) =>
      previewRequest<MailboxFeedbackActionResponse>(
        client,
        'post',
        `/api/v1/network/studies/mailboxes/messages/${input.id}/unassign`,
        { body: { reviewedBy: input.reviewedBy, reason: 'Desvinculado manualmente pelo usuário' } },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface MarkNotProjectInput {
  readonly id: string;
  readonly reviewedBy: string;
}

export function useMarkMessageNotProject() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: MarkNotProjectInput) =>
      previewRequest<MailboxFeedbackActionResponse>(
        client,
        'post',
        `/api/v1/network/studies/mailboxes/messages/${input.id}/mark-not-project`,
        {
          body: {
            reviewedBy: input.reviewedBy,
            reason: 'Marcado pelo humano como fora de escopo orçamentário',
          },
        },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface CreateDossierInput {
  readonly code: string;
  readonly studyCode: string;
  readonly title: string;
  readonly sponsorName: string;
  readonly croName: string;
  readonly currency: string;
  readonly exchangeRateBrl: number;
  readonly overheadPercent: number;
  readonly totalBaselineBrl: number;
  readonly totalNegotiatedBrl: number;
  readonly passThroughTotalBrl: number;
  readonly sourceMessageId?: string;
}

export function useCreateContractDossier() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: CreateDossierInput) =>
      previewRequest<CreateContractDossierResultResponse>(
        client,
        'post',
        '/api/v1/network/studies/dossiers',
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface SimulateDossierInput {
  readonly code: string;
  readonly totalNegotiatedBrl: number;
  readonly overheadPercent: number;
  readonly status: string;
}

export function useSimulateDossier() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: SimulateDossierInput) =>
      previewRequest<StudyContractDossierItem>(
        client,
        'post',
        `/api/v1/network/studies/dossiers/${input.code}/simulate`,
        {
          body: {
            totalNegotiatedBrl: input.totalNegotiatedBrl,
            overheadPercent: input.overheadPercent,
            status: input.status,
          },
        },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface CreateProcedureInput {
  readonly code: string;
  readonly namePtBr: string;
  readonly nameEnUs: string;
  readonly category: string;
  readonly defaultDurationMinutes: number;
  readonly baseUnitCostBrl: number;
  readonly regionCityUf?: string | undefined;
  readonly attendingCenterName?: string | undefined;
}

export function useCreateProcedure() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: CreateProcedureInput) =>
      previewRequest<ProcedureDefinitionItem>(
        client,
        'post',
        '/api/v1/network/studies/procedures',
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface ProcedureCityPriceItem {
  readonly id: string;
  readonly procedureCode: string;
  readonly cityUf: string;
  readonly unitMeasure: string;
  readonly unitPriceBrl: number;
  readonly version: number;
  readonly updatedAtIso: string;
  readonly updatedByEmail: string;
  readonly changeReason: string;
}

export interface MultiLlmCouncilOpinionDto {
  readonly provider: string;
  readonly modelName: string;
  readonly verdict: string;
  readonly recommendedTotalBrl: number;
  readonly keyJustification: string;
}

export interface MultiLlmCouncilRunDto {
  readonly runId: string;
  readonly studyCode: string;
  readonly executedAtIso: string;
  readonly consensusDecision: string;
  readonly confidencePercent: number;
  readonly opinions: readonly MultiLlmCouncilOpinionDto[];
}

export interface StudyChatMessageDto {
  readonly id: string;
  readonly studyCode: string;
  readonly authorEmail: string;
  readonly authorRole: string;
  readonly userMessage: string;
  readonly aiReply: string;
  readonly scope: string;
  readonly globalApprovalStatus: string;
  readonly createdAtIso: string;
}

export interface ProtocolCritiqueItemDto {
  readonly id: string;
  readonly studyCode: string;
  readonly title: string;
  readonly category: string;
  readonly severity: string;
  readonly status: string;
  readonly estimatedExposureBrl: number;
  readonly protocolReference: string;
  readonly discrepancyDescription: string;
  readonly recommendedAction: string;
  readonly distilledGlobalRule: string;
  readonly globalRuleStatus: string;
}

export interface StudyFinancialPaymentItemDto {
  readonly id: string;
  readonly studyCode: string;
  readonly paymentDate: string;
  readonly category: string;
  readonly procedureOrVisitCode: string;
  readonly patientRef: string;
  readonly originalAmount: number;
  readonly currency: string;
  readonly frozenPtaxRate: number;
  readonly paidAmountBrl: number;
  readonly expectedBudgetBrl: number;
  readonly invoiceNumber: string;
  readonly daysToReceive: number;
  readonly status: string;
}

export interface CashflowMonthlyPointDto {
  readonly monthLabel: string;
  readonly optimisticCumulativeBrl: number;
  readonly regularCumulativeBrl: number;
  readonly pessimisticCumulativeBrl: number;
}

export interface CashflowCriticalFactorDto {
  readonly factorName: string;
  readonly impactLevel: string;
  readonly watchingRule: string;
}

export interface StudyCashflowProjectionDto {
  readonly studyCode: string;
  readonly targetPatients: number;
  readonly durationMonths: number;
  readonly overheadRegularPercent: number;
  readonly netReceiptDaysRegular: number;
  readonly screenFailureRegularPercent: number;
  readonly optimisticNetBalanceBrl: number;
  readonly optimisticMarginPercent: number;
  readonly regularNetBalanceBrl: number;
  readonly regularMarginPercent: number;
  readonly pessimisticNetBalanceBrl: number;
  readonly pessimisticMarginPercent: number;
  readonly spreadRiskBrl: number;
  readonly monthlyPoints: readonly CashflowMonthlyPointDto[];
  readonly criticalFactors: readonly CashflowCriticalFactorDto[];
  readonly coordinatorGuardrails: readonly string[];
}

export interface GlobalAiRuleItemDto {
  readonly id: string;
  readonly studyCode: string;
  readonly ruleTitle: string;
  readonly ruleText: string;
  readonly originModule: string;
  readonly proposedByEmail: string;
  readonly status: 'APPROVED_GLOBAL' | 'PENDING_GLOBAL_APPROVAL';
  readonly approvedByEmail: string | null;
  readonly updatedAtIso: string;
}

export interface BudgetEmailDraftChangeItemDto {
  readonly field: 'TO' | 'CC' | 'SUBJECT' | 'BODY_LINE' | 'BUDGET_VALUE';
  readonly description: string;
  readonly originalValue: string;
  readonly newValue: string;
}

export interface BudgetEmailDraftRevisionDto {
  readonly revisionId: string;
  readonly revisionNumber: number;
  readonly modifiedAtIso: string;
  readonly modifiedByEmail: string;
  readonly source: 'MAILBOX_UI' | 'MICROSOFT_GRAPH_SYNC';
  readonly summary: string;
  readonly changes: readonly BudgetEmailDraftChangeItemDto[];
  readonly snapshotTo: string;
  readonly snapshotCc: string;
  readonly snapshotSubject: string;
  readonly snapshotBody: string;
}

export interface BudgetEmailDraftItemDto {
  readonly id: string;
  readonly graphDraftMessageId: string;
  readonly studyCode: string;
  readonly dossierId: string;
  readonly mailboxAddress: string;
  readonly status:
    'ORIGINAL_DRAFT' | 'MODIFIED_IN_MAILBOX' | 'SENT_ORIGINAL' | 'SENT_WITH_MODIFICATIONS';
  readonly createdAtIso: string;
  readonly createdByEmail: string;
  readonly updatedAtIso: string;
  readonly updatedByEmail: string;
  readonly originalTo: string;
  readonly originalCc: string;
  readonly originalSubject: string;
  readonly originalBody: string;
  readonly currentTo: string;
  readonly currentCc: string;
  readonly currentSubject: string;
  readonly currentBody: string;
  readonly isModifiedFromOriginal: boolean;
  readonly latestChanges: readonly BudgetEmailDraftChangeItemDto[];
  readonly revisions: readonly BudgetEmailDraftRevisionDto[];
}

export interface StudyWorkspaceBundleDto {
  readonly studyCode: string;
  readonly cityUf: string;
  readonly studyBudgetTableVersion: number;
  readonly holdingCurrentTableVersion: number;
  readonly isContractSignedFrozen: boolean;
  readonly hasNewerHoldingPriceTable: boolean;
  readonly dismissedKeepProposalVersion: boolean;
  readonly currentNegotiatedBrl: number;
  readonly recalculatedWithNewHoldingTableBrl: number;
  readonly priceTableAlertMessage: string;
  readonly latestCouncilRun: MultiLlmCouncilRunDto;
  readonly chatMessages: readonly StudyChatMessageDto[];
  readonly protocolCritiques: readonly ProtocolCritiqueItemDto[];
  readonly financialPayments: readonly StudyFinancialPaymentItemDto[];
  readonly cashflowProjection: StudyCashflowProjectionDto;
  readonly budgetEmailDrafts?: readonly BudgetEmailDraftItemDto[];
}

export function useProcedureCityPrices() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'procedures', 'city-prices'],
    queryFn: () =>
      previewRequest<readonly ProcedureCityPriceItem[]>(
        client,
        'get',
        '/api/v1/network/studies/procedures/city-prices',
      ),
    meta: { silent: true },
  });
}

export interface UpsertCityPriceInput {
  readonly procedureCode: string;
  readonly cityUf: string;
  readonly unitMeasure: string;
  readonly unitPriceBrl: number;
  readonly updatedByEmail: string;
  readonly changeReason: string;
}

export function useUpsertProcedureCityPrice() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: UpsertCityPriceInput) =>
      previewRequest<ProcedureCityPriceItem>(
        client,
        'post',
        `/api/v1/network/studies/procedures/${input.procedureCode}/city-prices`,
        {
          body: {
            cityUf: input.cityUf,
            unitMeasure: input.unitMeasure,
            unitPriceBrl: input.unitPriceBrl,
            updatedByEmail: input.updatedByEmail,
            changeReason: input.changeReason,
          },
        },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useStudyWorkspace(studyCode: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'dossiers', studyCode, 'workspace'],
    queryFn: () =>
      previewRequest<StudyWorkspaceBundleDto>(
        client,
        'get',
        `/api/v1/network/studies/dossiers/${studyCode}/workspace`,
      ),
    enabled: Boolean(studyCode),
    meta: { silent: true },
  });
}

export interface SyncHoldingPriceTableInput {
  readonly studyCode: string;
  readonly action: 'APPLY_NEW_HOLDING_TABLE' | 'KEEP_PROPOSAL_VERSION';
}

export function useSyncHoldingPriceTable() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: SyncHoldingPriceTableInput) =>
      previewRequest<StudyWorkspaceBundleDto>(
        client,
        'post',
        `/api/v1/network/studies/dossiers/${input.studyCode}/sync-holding-prices`,
        { body: { action: input.action } },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useRunStudyCouncil() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (studyCode: string) =>
      previewRequest<MultiLlmCouncilRunDto>(
        client,
        'post',
        `/api/v1/network/studies/dossiers/${studyCode}/council/run`,
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface CreateProtocolCritiqueInput {
  readonly studyCode: string;
  readonly title: string;
  readonly category: string;
  readonly severity: string;
  readonly estimatedExposureBrl: number;
  readonly protocolReference: string;
  readonly discrepancyDescription: string;
  readonly recommendedAction: string;
  readonly distilledGlobalRule: string;
  readonly promoteGlobal: boolean;
  readonly actorEmail: string;
}

export function useCreateProtocolCritique() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: CreateProtocolCritiqueInput) =>
      previewRequest<ProtocolCritiqueItemDto>(
        client,
        'post',
        `/api/v1/network/studies/dossiers/${input.studyCode}/critiques`,
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface CreateStudyPaymentInput {
  readonly studyCode: string;
  readonly paymentDate: string;
  readonly category: string;
  readonly procedureOrVisitCode: string;
  readonly patientRef: string;
  readonly originalAmount: number;
  readonly currency: string;
  readonly frozenPtaxRate: number;
  readonly expectedBudgetBrl: number;
  readonly invoiceNumber: string;
  readonly daysToReceive: number;
}

export function useCreateStudyPayment() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: CreateStudyPaymentInput) =>
      previewRequest<StudyFinancialPaymentItemDto>(
        client,
        'post',
        `/api/v1/network/studies/dossiers/${input.studyCode}/payments`,
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface RunCashflowProjectionInput {
  readonly studyCode: string;
  readonly targetPatients: number;
  readonly durationMonths: number;
  readonly overheadRegularPercent: number;
  readonly netReceiptDaysRegular: number;
  readonly screenFailureRegularPercent: number;
}

export function useRunStudyCashflowProjection() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: RunCashflowProjectionInput) =>
      previewRequest<StudyCashflowProjectionDto>(
        client,
        'post',
        `/api/v1/network/studies/dossiers/${input.studyCode}/cashflow/run`,
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useGlobalAiRules() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'global-rules'],
    queryFn: () =>
      previewRequest<readonly GlobalAiRuleItemDto[]>(
        client,
        'get',
        '/api/v1/network/studies/global-rules',
      ),
    meta: { silent: true },
  });
}

export interface CreateGlobalRuleInput {
  readonly studyCode: string;
  readonly ruleTitle: string;
  readonly ruleText: string;
  readonly originModule: string;
  readonly actorEmail: string;
}

export function useCreateGlobalAiRule() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: CreateGlobalRuleInput) =>
      previewRequest<GlobalAiRuleItemDto>(client, 'post', '/api/v1/network/studies/global-rules', {
        body: input,
      }),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface ApproveGlobalRuleInput {
  readonly ruleId: string;
  readonly approverEmail: string;
}

export function useApproveGlobalAiRule() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: ApproveGlobalRuleInput) =>
      previewRequest<GlobalAiRuleItemDto>(
        client,
        'post',
        `/api/v1/network/studies/global-rules/${input.ruleId}/approve`,
        { body: { approverEmail: input.approverEmail } },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

// ============================================================================
// GOOGLE RESEARCH HARNESS² (c1..c8 · LongTermMEMORY.md & EXPERIENCE.md)
// ============================================================================
export interface Harness2ComponentStatusDto {
  readonly code: string;
  readonly fileOrDir: string;
  readonly title: string;
  readonly roleInPlatform: string;
  readonly tokenOrItemMetric: string;
  readonly validationStatus: 'VALIDATED_ON_SURFACE';
}

export interface Harness2EvolutionStateDto {
  readonly surfaceName: string;
  readonly domain: string;
  readonly evolutionGeneration: number;
  readonly alwaysOnTokensUsed: number;
  readonly alwaysOnTokensCap: number;
  readonly activeSkills: readonly string[];
  readonly activeSubAgents: readonly string[];
  readonly componentsC1ToC8: readonly Harness2ComponentStatusDto[];
  readonly longTermMemoryExcerpt: string;
  readonly experienceLogExcerpt: string;
}

export interface EvolveHarness2Input {
  readonly cue: string;
  readonly lesson: string;
  readonly originModule: string;
  readonly actorEmail: string;
}

export function useHarness2EvolutionState() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'harness2'],
    queryFn: () =>
      previewRequest<Harness2EvolutionStateDto>(client, 'get', '/api/v1/network/studies/harness2'),
    meta: { silent: true },
  });
}

export function useEvolveHarness2() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EvolveHarness2Input) =>
      previewRequest<Harness2EvolutionStateDto>(
        client,
        'post',
        '/api/v1/network/studies/harness2/evolve',
        { body: input },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['network', 'studies'] });
    },
    meta: { silent: true },
  });
}

// ============================================================================
// CONCILIAÇÃO DE NOTAS FISCAIS DE REEMBOLSO NA CAIXA FINANCEIRO@GIYUS.COM.BR
// TOPOLOGIA LOCAL GREENFIELD (MongoDB Community) vs REMOTO PRODUÇÃO (MongoDB Atlas)
// ============================================================================
export interface FinanceMailboxEnvironmentTopologyDto {
  readonly activeProfile: string;
  readonly localEnvironmentMode: string;
  readonly localMongoConnectionUri: string;
  readonly localDatabaseName: string;
  readonly localBootstrapStrategy: string;
  readonly remoteEnvironmentMode: string;
  readonly remoteMongoAtlasConnectionMasked: string;
  readonly remoteDatabaseName: string;
  readonly remoteBootstrapStrategy: string;
  readonly sharedMailboxAddress: string;
  readonly runtimeFramework: string;
}

export interface FinanceMailboxReimbursementItemDto {
  readonly id: string;
  readonly graphMessageId: string;
  readonly mailboxAddress: string;
  readonly receivedAtIso: string;
  readonly senderName: string;
  readonly senderEmail: string;
  readonly subject: string;
  readonly bodyPreview: string;
  readonly attachments: readonly string[];
  readonly invoiceNumber: string;
  readonly documentKind: string;
  readonly issuerCnpj: string;
  readonly invoiceAmountBrl: number;
  readonly isReimbursementCandidate: boolean;
  readonly aiDetectionConfidencePercent: number;
  readonly reimbursementCategoryCode: string;
  readonly reimbursementCategoryLabel: string;
  readonly studyCode: string;
  readonly sponsorOrCroName: string;
  readonly contractClauseReference: string;
  readonly studyPassThroughCeilingBrl: number;
  readonly studyPassThroughRemainingBrl: number;
  readonly billingTaxTreatment: string;
  readonly grossUpTaxPercent: number;
  readonly holdbackPercent: number;
  readonly rebillableToSponsorAmountBrl: number;
  readonly clinicalOriginReference: string;
  readonly reconciliationStatus:
    | 'AGUARDANDO_CONCILIACAO_FINANCEIRA'
    | 'CONCILIADA_REEMBOLSO_PASS_THROUGH'
    | 'CLASSIFICADA_DESPESA_ORDINARIA_UNIDADE'
    | 'PENDENTE_CARTA_CORRECAO_OU_XML';
  readonly accountingAccumulatorCode: string | null;
  readonly reconciledByEmail: string | null;
  readonly reconciledAtIso: string | null;
  readonly auditSha256Hash: string;
  readonly reconciliationNotes: string;
}

export interface FinanceMailboxReimbursementDashboardDto {
  readonly topology: FinanceMailboxEnvironmentTopologyDto;
  readonly totalFinanceEmailsCount: number;
  readonly pendingReimbursementConciliationCount: number;
  readonly reconciledAsReimbursementCount: number;
  readonly reclassifiedAsOrdinaryExpenseCount: number;
  readonly totalPendingReimbursementAmountBrl: number;
  readonly totalReconciledReimbursementAmountBrl: number;
  readonly items: readonly FinanceMailboxReimbursementItemDto[];
}

export interface ReconcileFinanceMailboxInvoiceInput {
  readonly id: string;
  readonly decision:
    | 'CONCILIAR_COMO_REEMBOLSO_PASS_THROUGH'
    | 'CLASSIFICAR_DESPESA_ORDINARIA_NAO_REEMBOLSO'
    | 'SOLICITAR_CARTA_CORRECAO_FISCAL';
  readonly studyCode: string;
  readonly billingTaxTreatment: string;
  readonly reconciledByEmail?: string;
  readonly notes?: string;
}

export interface SimulateIncomingFinanceMailboxEmailInput {
  readonly senderName: string;
  readonly senderEmail: string;
  readonly subject: string;
  readonly bodyPreview: string;
  readonly invoiceNumber: string;
  readonly documentKind: string;
  readonly issuerCnpj: string;
  readonly invoiceAmountBrl: number;
  readonly suggestedStudyCode: string;
  readonly attachments?: readonly string[];
}

export function useFinanceMailboxReimbursements() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'mailboxes', 'finance-reimbursements'],
    queryFn: () =>
      previewRequest<FinanceMailboxReimbursementDashboardDto>(
        client,
        'get',
        '/api/v1/network/studies/mailboxes/finance-reimbursements',
      ),
    meta: { silent: true },
  });
}

export function useReconcileFinanceMailboxInvoice() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ReconcileFinanceMailboxInvoiceInput) =>
      previewRequest<FinanceMailboxReimbursementItemDto>(
        client,
        'post',
        `/api/v1/network/studies/mailboxes/finance-reimbursements/${input.id}/reconcile`,
        {
          body: {
            decision: input.decision,
            studyCode: input.studyCode,
            billingTaxTreatment: input.billingTaxTreatment,
            reconciledByEmail: input.reconciledByEmail ?? 'syllas@giyus.com.br',
            notes: input.notes,
          },
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['network', 'studies'] });
    },
    meta: { silent: true },
  });
}

export function useSimulateIncomingFinanceMailboxEmail() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SimulateIncomingFinanceMailboxEmailInput) =>
      previewRequest<FinanceMailboxReimbursementItemDto>(
        client,
        'post',
        '/api/v1/network/studies/mailboxes/finance-reimbursements/simulate-incoming',
        { body: input },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['network', 'studies'] });
    },
    meta: { silent: true },
  });
}

export function useMailboxBudgetEmailDrafts() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'mailboxes', 'drafts'],
    queryFn: () =>
      previewRequest<readonly BudgetEmailDraftItemDto[]>(
        client,
        'get',
        '/api/v1/network/studies/mailboxes/drafts',
      ),
    meta: { silent: true },
  });
}

export interface CreateBudgetEmailDraftInput {
  readonly studyCode: string;
  readonly createdByEmail: string;
  readonly mailboxAddress?: string;
}

export function useCreateBudgetEmailDraft() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: CreateBudgetEmailDraftInput) =>
      previewRequest<BudgetEmailDraftItemDto>(
        client,
        'post',
        `/api/v1/network/studies/dossiers/${input.studyCode}/email-drafts`,
        {
          body: {
            createdByEmail: input.createdByEmail,
            mailboxAddress: input.mailboxAddress ?? 'contratos@giyus.com.br',
          },
        },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface UpdateMailboxBudgetEmailDraftInput {
  readonly id: string;
  readonly currentTo: string;
  readonly currentCc: string;
  readonly currentSubject: string;
  readonly currentBody: string;
  readonly updatedByEmail: string;
  readonly action?: 'SAVE_CHANGES' | 'SEND_EMAIL' | 'RESTORE_ORIGINAL';
}

export function useUpdateMailboxBudgetEmailDraft() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: UpdateMailboxBudgetEmailDraftInput) =>
      previewRequest<BudgetEmailDraftItemDto>(
        client,
        'put',
        `/api/v1/network/studies/mailboxes/drafts/${input.id}`,
        {
          body: {
            currentTo: input.currentTo,
            currentCc: input.currentCc,
            currentSubject: input.currentSubject,
            currentBody: input.currentBody,
            updatedByEmail: input.updatedByEmail,
            action: input.action ?? 'SAVE_CHANGES',
          },
        },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export type StudyProcedureNormalizationStatus =
  | 'AUTO_MATCHED_GLOBAL'
  | 'PENDING_HUMAN_DECISION'
  | 'HUMAN_APPROVED_CREATED_GLOBAL'
  | 'HUMAN_ALIGNED_EXISTING_GLOBAL';

export interface IncomingStudyProcedureNormalizationItemDto {
  readonly id: string;
  readonly studyCode: string;
  readonly protocolCode: string;
  readonly studyRawCode: string;
  readonly studyRawName: string;
  readonly proposedGlobalCode: string;
  readonly proposedGlobalNamePtBr: string;
  readonly proposedGlobalNameEnUs: string;
  readonly category: string;
  readonly defaultDurationMinutes: number;
  readonly studyUnitBaselineBrl: number;
  readonly studyUnitNegotiatedBrl: number;
  readonly occurrencesInStudy: number;
  readonly visitRefsSummary: string;
  readonly activeUsedRegionCityUf: string;
  readonly attendingResearchCenterName?: string | undefined;
  readonly suggestedExistingGlobalCode: string | null;
  readonly normalizationStatus: StudyProcedureNormalizationStatus;
  readonly matchedGlobalProcedureCode: string | null;
  readonly matchedGlobalProcedureName: string | null;
  readonly resolvedByEmail: string | null;
  readonly resolvedAtIso: string | null;
  readonly resolutionNotes: string | null;
}

export interface StudyProceduresNormalizationDashboardDto {
  readonly incomingStudiesCount: number;
  readonly totalStudyProceduresCount: number;
  readonly autoNormalizedCount: number;
  readonly pendingHumanDecisionCount: number;
  readonly humanApprovedCreatedGlobalCount: number;
  readonly humanAlignedExistingGlobalCount: number;
  readonly activeUsedRegions: readonly string[];
  readonly items: readonly IncomingStudyProcedureNormalizationItemDto[];
}

export interface ResolveIncomingStudyProcedureInput {
  readonly itemId: string;
  readonly decision: 'APPROVE_CREATE_GLOBAL' | 'ALIGN_WITH_EXISTING_GLOBAL';
  readonly targetGlobalProcedureCode?: string;
  readonly customGlobalCode?: string;
  readonly customGlobalNamePtBr?: string;
  readonly customBaseUnitCostBrl?: number;
  readonly actorEmail: string;
}

export function useStudyProceduresNormalizationDashboard() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'procedures', 'normalization-queue'],
    queryFn: () =>
      previewRequest<StudyProceduresNormalizationDashboardDto>(
        client,
        'get',
        '/api/v1/network/studies/procedures/normalization-queue',
      ),
    meta: { silent: true },
  });
}

export function useResolveIncomingStudyProcedure() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: ResolveIncomingStudyProcedureInput) =>
      previewRequest<StudyProceduresNormalizationDashboardDto>(
        client,
        'post',
        '/api/v1/network/studies/procedures/normalization-queue/resolve',
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface DetectedEntityCandidateDto {
  readonly kind: 'Sponsor' | 'Cro';
  readonly code: string;
  readonly name: string;
  readonly country: string;
  readonly contactEmail: string;
  readonly cnpjOrTaxId: string;
  readonly detectionSource: string;
  readonly isRegisteredInSeparateRegistry: boolean;
  readonly registeredOrganizationId: string | null;
}

export interface DetectedSponsorAndCroPairDto {
  readonly studyCode: string;
  readonly protocolCode: string;
  readonly studyTitle: string;
  readonly sourceMessagesCount: number;
  readonly latestMessageSubject: string;
  readonly detectedSponsor: DetectedEntityCandidateDto;
  readonly detectedCro: DetectedEntityCandidateDto;
}

export interface SponsorAndCroAutoDetectionDashboardDto {
  readonly sponsorsRegistry: readonly OrganizationItem[];
  readonly crosRegistry: readonly OrganizationItem[];
  readonly detectedPairs: readonly DetectedSponsorAndCroPairDto[];
  readonly totalDetectedStudiesCount: number;
  readonly pendingSponsorsToRegisterCount: number;
  readonly pendingCrosToRegisterCount: number;
}

export interface AutoRegisterSponsorOrCroInput {
  readonly studyCode?: string;
  readonly target:
    | 'SPONSOR_ONLY'
    | 'CRO_ONLY'
    | 'BOTH_SEPARATE_REGISTRIES'
    | 'ALL_DETECTED_SEPARATE_REGISTRIES'
    | 'MANUAL_SEPARATE_ENTRY';
  readonly manualKind?: 'Sponsor' | 'Cro';
  readonly customCode?: string;
  readonly customName?: string;
  readonly customCountry?: string;
  readonly customContactEmail?: string;
  readonly actorEmail?: string;
}

export function useSponsorAndCroAutoDetectionDashboard() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'organizations', 'auto-detected'],
    queryFn: () =>
      previewRequest<SponsorAndCroAutoDetectionDashboardDto>(
        client,
        'get',
        '/api/v1/network/studies/organizations/auto-detected',
      ),
    meta: { silent: true },
  });
}

export function useAutoRegisterSponsorOrCro() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: AutoRegisterSponsorOrCroInput) =>
      previewRequest<SponsorAndCroAutoDetectionDashboardDto>(
        client,
        'post',
        '/api/v1/network/studies/organizations/register',
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface ClinicalSpecialtyItemDto {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly category: string;
  readonly active: boolean;
  readonly updatedAtIso?: string;
}

export interface ManageClinicalSpecialtyInput {
  readonly action: 'CREATE' | 'UPDATE' | 'DELETE';
  readonly id?: string | undefined;
  readonly code?: string | undefined;
  readonly name?: string | undefined;
  readonly category?: string | undefined;
  readonly active?: boolean | undefined;
}

export interface ManageClinicalSpecialtyResponseDto {
  readonly ok: boolean;
  readonly message?: string | undefined;
  readonly specialties: readonly ClinicalSpecialtyItemDto[];
}

export interface ClinicalHealthFunctionItemDto {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly councilAcronym: string;
  readonly active: boolean;
  readonly updatedAtIso?: string | undefined;
}

export interface ManageClinicalHealthFunctionInput {
  readonly action: 'CREATE' | 'UPDATE' | 'DELETE';
  readonly id?: string | undefined;
  readonly code?: string | undefined;
  readonly name?: string | undefined;
  readonly councilAcronym?: string | undefined;
  readonly active?: boolean | undefined;
}

export interface ManageClinicalHealthFunctionResponseDto {
  readonly ok: boolean;
  readonly message?: string | undefined;
  readonly healthFunctions: readonly ClinicalHealthFunctionItemDto[];
}

export interface StudySubInvestigatorMemberDto {
  readonly name: string;
  readonly crm: string;
  readonly cpf?: string;
  readonly specialty: string;
  readonly secondarySpecialties?: readonly string[];
  readonly email: string;
  readonly commercialEmail?: string;
  readonly personalEmail?: string;
  readonly mobilePhone?: string;
  readonly isPrincipalInvestigator?: boolean;
  readonly isSubInvestigator?: boolean;
  readonly piTherapeuticAreas?: readonly string[];
  readonly subTherapeuticAreas?: readonly string[];
  readonly relatedCenters?: readonly string[];
}

export interface StudyCoordinatorMemberDto {
  readonly name: string;
  readonly cpf?: string;
  readonly birthDate?: string;
  readonly level?: 'Júnior' | 'Pleno' | 'Sênior';
  readonly isHealthProfessional?: boolean;
  readonly healthProfessionalType?: string;
  readonly councilRegistrationNumber?: string;
  readonly role: string;
  readonly email: string;
  readonly corporateEmail?: string;
  readonly personalEmail?: string;
  readonly mobilePhone?: string;
}

export type ResearchCenterClassificationType =
  'CENTRO_NAO_GIYUS' | 'CENTRO_GIYUS' | 'CENTRO_PARCEIRO_GIYUS' | 'GIYUS_HOLDING';

export const RESEARCH_CENTER_CLASSIFICATIONS: Readonly<
  Record<
    ResearchCenterClassificationType,
    {
      readonly code: ResearchCenterClassificationType;
      readonly label: string;
      readonly shortLabel: string;
      readonly allowMultiple: boolean;
      readonly maxAllowedLabel: string;
      readonly paymentAndGovernanceRule: string;
    }
  >
> = {
  CENTRO_NAO_GIYUS: {
    code: 'CENTRO_NAO_GIYUS',
    label:
      'Centro Não-GIYUS (Estudo realizado no centro parceiro · Coordenadoras / Pessoas envolvidas recebem diretamente)',
    shortLabel: 'Centro Não-GIYUS',
    allowMultiple: true,
    maxAllowedLabel: 'Múltiplos permitidos (1..N)',
    paymentAndGovernanceRule:
      'Realizamos o estudo no centro parceiro e as coordenadoras / pessoas envolvidas recebem diretamente.',
  },
  CENTRO_GIYUS: {
    code: 'CENTRO_GIYUS',
    label: 'Centro GIYUS (Centro Próprio da Rede GIYUS)',
    shortLabel: 'Centro GIYUS',
    allowMultiple: true,
    maxAllowedLabel: 'Múltiplos permitidos (1..N)',
    paymentAndGovernanceRule:
      'Centro próprio da rede GIYUS (Tenant GIYUS) com operação e faturamento sob estrutura própria.',
  },
  CENTRO_PARCEIRO_GIYUS: {
    code: 'CENTRO_PARCEIRO_GIYUS',
    label: 'Centro Parceiro GIYUS (Parceria Institucional GIYUS)',
    shortLabel: 'Centro Parceiro GIYUS',
    allowMultiple: true,
    maxAllowedLabel: 'Múltiplos permitidos (1..N)',
    paymentAndGovernanceRule:
      'Centro parceiro associado à GIYUS (múltiplas unidades parceiras permitidas).',
  },
  GIYUS_HOLDING: {
    code: 'GIYUS_HOLDING',
    label: 'GIYUS Holding (Matriz Única — Só pode ser 1)',
    shortLabel: 'GIYUS Holding (Única · Máx. 1)',
    allowMultiple: false,
    maxAllowedLabel: 'Única (Só pode ser 1)',
    paymentAndGovernanceRule:
      'Holding Matriz GIYUS — unicidade estrita (só pode existir 1 cadastro desta classificação em toda a plataforma).',
  },
};

export interface StudyTeamAndCenterBindingDto {
  readonly studyCode: string;
  readonly piName: string;
  readonly piCrm: string;
  readonly piSpecialty: string;
  readonly piEmail: string;
  readonly subInvestigatorName: string;
  readonly subInvestigatorCrm: string;
  readonly subInvestigatorSpecialty: string;
  readonly subInvestigatorEmail: string;
  readonly subInvestigators?: readonly StudySubInvestigatorMemberDto[];
  readonly coordinatorName: string;
  readonly coordinatorRole: string;
  readonly coordinatorEmail: string;
  readonly coordinators?: readonly StudyCoordinatorMemberDto[];
  readonly researchCenterName: string;
  readonly researchCenterLegalName: string;
  readonly researchCenterCnpj: string;
  readonly researchCenterCnes: string;
  readonly researchCenterCityUf: string;
  readonly researchCenterAddress: string;
  readonly researchCenterBankSummary: string;
  readonly researchCenterClassification?: ResearchCenterClassificationType;
  readonly researchCenterClassificationLabel?: string;
  readonly researchCenterPaymentFlowRule?: string;
  readonly isAutoFilled: boolean;
  readonly autoFilledFieldsCount: number;
  readonly autoFilledContractPreambleText: string;
  readonly autoFilledDraftRecipientsTo: string;
  readonly autoFilledDraftRecipientsCc: string;
  readonly lastLinkedAtIso: string;
  readonly lastLinkedByEmail: string;
  readonly explicitlyBoundByUser?: boolean;
}

export interface BigDataCorpQsaMemberDto {
  readonly name: string;
  readonly taxId: string;
  readonly role: string;
  readonly startDate: string;
}

export interface CnesRecordDto {
  readonly cnes: string;
  readonly cnesCode?: string | undefined;
  readonly unitName: string;
  readonly sanitaryLicenseTypeAndNumber: string;
  readonly sanitaryLicenseStatus?: string | undefined;
  readonly relatedToDescription: string;
  readonly unitType?: string | undefined;
  readonly sanitaryPermitNumber?: string | undefined;
  readonly sanitaryPermitDate?: string | undefined;
  readonly sanitaryPermitIssuer?: string | undefined;
  readonly updatedAt?: string | undefined;
}

export interface BigDataCorpCnaeActivityDto {
  readonly code: string;
  readonly description: string;
  readonly isMain: boolean;
}

export interface BigDataCorpEstablishmentDto {
  readonly cnpj: string;
  readonly cnpjDigits: string;
  readonly cnpjRoot: string;
  readonly branchOrder: string;
  readonly isHeadquarter: boolean;
  readonly establishmentTypeLabel: string;
  readonly officialName: string;
  readonly tradeName: string;
  readonly foundedDate: string;
  readonly foundedDateIso: string;
  readonly ageYears: number | null;
  readonly taxIdStatus: string;
  readonly taxIdStatusDate: string;
  readonly taxRegime: string;
  readonly companyTypeReceita: string;
  readonly legalNatureCode: string;
  readonly legalNatureDescription: string;
  readonly legalNatureFormatted: string;
  readonly capitalSocialFormatted: string;
  readonly mainCnaeFormatted: string;
  readonly secondaryCnaesFormatted: readonly string[];
  readonly allCnaesFormatted: string;
  readonly cnaeActivities: readonly BigDataCorpCnaeActivityDto[];
  readonly qsaMembers: readonly BigDataCorpQsaMemberDto[];
  readonly qsaSummaryFormatted: string;
  readonly addressLine: string;
  readonly street?: string;
  readonly number?: string;
  readonly complement?: string;
  readonly neighborhood: string;
  readonly zipCode: string;
  readonly city: string;
  readonly state: string;
  readonly cityUf: string;
  readonly fullAddressFormatted: string;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly phonesFormatted: readonly string[];
  readonly emailsFormatted: readonly string[];
  readonly cnes?: string;
  readonly cnesUnitType?: string;
  readonly cnesSanitaryPermit?: string;
  readonly cnesRecords?: readonly CnesRecordDto[];
  readonly sourceProvider: string;
  readonly queriedAtIso: string;
}

export type BankAccountStandardType =
  | 'BRASIL_FEBRABAN_PIX'
  | 'EUROPA_SEPA_IBAN'
  | 'EUA_US_ACH_WIRE'
  | 'REINO_UNIDO_UK_BACS_CHAPS'
  | 'INTERNACIONAL_SWIFT_CAMBIO_BR';

export interface ResearchCenterBankAccountDto {
  readonly id: string;
  readonly label: string;
  readonly isPrimary: boolean;
  readonly bankStandard: BankAccountStandardType;
  readonly currency: 'BRL' | 'USD' | 'EUR' | 'GBP' | 'CHF';
  readonly bankCodeAndName: string;
  readonly ispbCode?: string | undefined;
  readonly branchNumber?: string | undefined;
  readonly accountNumber?: string | undefined;
  readonly accountType?: string | undefined;
  readonly pixKeyType?: 'CNPJ' | 'EMAIL' | 'TELEFONE' | 'CHAVE_ALEATORIA_EVP' | '' | undefined;
  readonly pixKey?: string | undefined;
  readonly beneficiaryName?: string | undefined;
  readonly beneficiaryTaxId?: string | undefined;
  readonly abaRoutingNumber?: string | undefined;
  readonly fedwireRoutingNumber?: string | undefined;
  readonly swiftBic?: string | undefined;
  readonly iban?: string | undefined;
  readonly sepaScheme?: string | undefined;
  readonly sepaCreditorId?: string | undefined;
  readonly ukSortCode?: string | undefined;
  readonly intermediaryBankName?: string | undefined;
  readonly intermediarySwiftBic?: string | undefined;
  readonly intermediaryAccountOrAba?: string | undefined;
  readonly bankCountry?: string | undefined;
  readonly notes?: string | undefined;
}

export const BANK_ACCOUNT_STANDARDS_CATALOG: Readonly<
  Record<
    BankAccountStandardType,
    {
      readonly code: BankAccountStandardType;
      readonly shortLabel: string;
      readonly label: string;
      readonly defaultCurrency: 'BRL' | 'USD' | 'EUR' | 'GBP' | 'CHF';
      readonly regionBadge: string;
      readonly standardSummary: string;
    }
  >
> = {
  BRASIL_FEBRABAN_PIX: {
    code: 'BRASIL_FEBRABAN_PIX',
    shortLabel: 'Brasil (Banco · Ag · CC · PIX)',
    label:
      'Padrão Brasil — FEBRABAN / SPB / PIX (Banco COMPE/ISPB, Agência, Conta Corrente e Chave PIX · BRL)',
    defaultCurrency: 'BRL',
    regionBadge: 'BRASIL · FEBRABAN / PIX (BRL)',
    standardSummary:
      'Código do Banco (COMPE 3 dígitos + ISPB), Agência, Conta Corrente PJ e Chave PIX (CNPJ/E-mail/Telefone/EVP).',
  },
  EUROPA_SEPA_IBAN: {
    code: 'EUROPA_SEPA_IBAN',
    shortLabel: 'Europa (SEPA · IBAN · BIC/SWIFT)',
    label: 'Padrão Europa — SEPA (Single Euro Payments Area) + IBAN + BIC/SWIFT + TARGET2 (EUR)',
    defaultCurrency: 'EUR',
    regionBadge: 'EUROPA · SEPA / IBAN / BIC (EUR)',
    standardSummary:
      'SEPA (Single Euro Payments Area — Área Única de Pagamentos em Euros), IBAN (ISO 13616), BIC/SWIFT (ISO 9362) e TARGET2.',
  },
  EUA_US_ACH_WIRE: {
    code: 'EUA_US_ACH_WIRE',
    shortLabel: 'EUA (ABA Routing · ACH · Fedwire · SWIFT)',
    label:
      'Padrão Estados Unidos — ABA Routing Number (ACH) + Fedwire + US Account Number + SWIFT (USD)',
    defaultCurrency: 'USD',
    regionBadge: 'EUA · ABA / ACH / FEDWIRE (USD)',
    standardSummary:
      'ABA Routing Number (9 dígitos ACH), Fedwire Routing (Domestic Wire), Account Number (DDA Checking) e SWIFT/BIC.',
  },
  REINO_UNIDO_UK_BACS_CHAPS: {
    code: 'REINO_UNIDO_UK_BACS_CHAPS',
    shortLabel: 'Reino Unido (UK Sort Code · BACS / Faster Payments · IBAN)',
    label:
      'Padrão Reino Unido — UK Sort Code (6 dígitos) + Account Number (8 dígitos) + IBAN GB + BACS/CHAPS (GBP)',
    defaultCurrency: 'GBP',
    regionBadge: 'UK · SORT CODE / BACS / IBAN (GBP)',
    standardSummary:
      'UK Sort Code (XX-XX-XX), 8-Digit Account Number, UK IBAN (GB), BIC/SWIFT e Faster Payments / BACS / CHAPS.',
  },
  INTERNACIONAL_SWIFT_CAMBIO_BR: {
    code: 'INTERNACIONAL_SWIFT_CAMBIO_BR',
    shortLabel: 'Câmbio Internacional / Offshore (SWIFT · IBAN BR · Correspondente)',
    label:
      'Padrão Remessa Internacional / Câmbio Brasil — SWIFT/BIC + IBAN BR (29 caracteres) + Banco Intermediário Correspondente (USD/EUR)',
    defaultCurrency: 'USD',
    regionBadge: 'INTERNACIONAL · SWIFT / IBAN BR / CORRESPONDENTE',
    standardSummary:
      'Fechamento de câmbio de Patrocinadores/CROs globais via SWIFT/BIC, IBAN Brasileiro (BR 29 posições) e Banco Correspondente no Exterior (Intermediary Bank).',
  },
};

export function formatResearchCenterBankAccountLine(acc: ResearchCenterBankAccountDto): string {
  const primaryPrefix = acc.isPrimary ? '[Principal] ' : '';
  const labelPrefix = acc.label ? `${acc.label}: ` : '';
  if (acc.bankStandard === 'BRASIL_FEBRABAN_PIX') {
    const parts = [
      acc.bankCodeAndName,
      acc.ispbCode ? `ISPB ${acc.ispbCode}` : '',
      acc.branchNumber ? `Ag. ${acc.branchNumber}` : '',
      acc.accountNumber ? `C/C ${acc.accountNumber}` : '',
      acc.accountType ? `(${acc.accountType})` : '',
      acc.pixKey ? `PIX ${acc.pixKeyType ? `${acc.pixKeyType}: ` : ''}${acc.pixKey}` : '',
    ].filter(Boolean);
    return `${primaryPrefix}${labelPrefix}[BRASIL/PIX ${acc.currency}] ${parts.join(' · ')}`;
  }
  if (acc.bankStandard === 'EUROPA_SEPA_IBAN') {
    const parts = [
      acc.bankCodeAndName,
      acc.sepaScheme ? `SEPA (${acc.sepaScheme})` : 'SEPA (Single Euro Payments Area)',
      acc.iban ? `IBAN ${acc.iban}` : '',
      acc.swiftBic ? `BIC/SWIFT ${acc.swiftBic}` : '',
      acc.sepaCreditorId ? `SEPA ID ${acc.sepaCreditorId}` : '',
      acc.intermediaryBankName
        ? `Intermediário: ${acc.intermediaryBankName}${acc.intermediarySwiftBic ? ` (${acc.intermediarySwiftBic})` : ''}`
        : '',
    ].filter(Boolean);
    return `${primaryPrefix}${labelPrefix}[EUROPA SEPA/IBAN ${acc.currency}] ${parts.join(' · ')}`;
  }
  if (acc.bankStandard === 'EUA_US_ACH_WIRE') {
    const parts = [
      acc.bankCodeAndName,
      acc.abaRoutingNumber ? `ABA/ACH ${acc.abaRoutingNumber}` : '',
      acc.fedwireRoutingNumber ? `Fedwire ${acc.fedwireRoutingNumber}` : '',
      acc.accountNumber ? `Account ${acc.accountNumber}` : '',
      acc.swiftBic ? `SWIFT ${acc.swiftBic}` : '',
      acc.intermediaryBankName
        ? `Correspondent: ${acc.intermediaryBankName}${acc.intermediarySwiftBic ? ` (${acc.intermediarySwiftBic})` : ''}`
        : '',
    ].filter(Boolean);
    return `${primaryPrefix}${labelPrefix}[EUA ACH/FEDWIRE ${acc.currency}] ${parts.join(' · ')}`;
  }
  if (acc.bankStandard === 'REINO_UNIDO_UK_BACS_CHAPS') {
    const parts = [
      acc.bankCodeAndName,
      acc.ukSortCode ? `Sort Code ${acc.ukSortCode}` : '',
      acc.accountNumber ? `Acc ${acc.accountNumber}` : '',
      acc.iban ? `IBAN ${acc.iban}` : '',
      acc.swiftBic ? `BIC/SWIFT ${acc.swiftBic}` : '',
    ].filter(Boolean);
    return `${primaryPrefix}${labelPrefix}[UK SORT CODE/BACS ${acc.currency}] ${parts.join(' · ')}`;
  }
  const parts = [
    acc.bankCodeAndName,
    acc.swiftBic ? `SWIFT/BIC ${acc.swiftBic}` : '',
    acc.iban ? `IBAN ${acc.iban}` : '',
    acc.branchNumber ? `Ag. ${acc.branchNumber}` : '',
    acc.accountNumber ? `Conta ${acc.accountNumber}` : '',
    acc.intermediaryBankName
      ? `Banco Correspondente: ${acc.intermediaryBankName}${acc.intermediarySwiftBic ? ` (SWIFT ${acc.intermediarySwiftBic})` : ''}${acc.intermediaryAccountOrAba ? ` · ${acc.intermediaryAccountOrAba}` : ''}`
      : '',
  ].filter(Boolean);
  return `${primaryPrefix}${labelPrefix}[INTERNACIONAL SWIFT/CÂMBIO ${acc.currency}] ${parts.join(' · ')}`;
}

export function formatResearchCenterBankAccountsSummary(
  accounts: readonly ResearchCenterBankAccountDto[],
): {
  readonly bankBrSummary: string;
  readonly bankIntlSummary: string;
  readonly fullCombinedSummary: string;
} {
  if (!accounts || accounts.length === 0) {
    return {
      bankBrSummary: '',
      bankIntlSummary: '',
      fullCombinedSummary: '',
    };
  }
  const brAccounts = accounts.filter((a) => a.bankStandard === 'BRASIL_FEBRABAN_PIX');
  const intlAccounts = accounts.filter((a) => a.bankStandard !== 'BRASIL_FEBRABAN_PIX');
  const bankBrSummary =
    brAccounts.length > 0
      ? brAccounts.map(formatResearchCenterBankAccountLine).join(' | ')
      : formatResearchCenterBankAccountLine(accounts[0]!);
  const bankIntlSummary =
    intlAccounts.length > 0
      ? intlAccounts.map(formatResearchCenterBankAccountLine).join(' | ')
      : 'Sem conta internacional cadastrada (liquidação exclusiva nacional BRL)';
  const fullCombinedSummary = accounts.map(formatResearchCenterBankAccountLine).join(' | ');
  return {
    bankBrSummary,
    bankIntlSummary,
    fullCombinedSummary,
  };
}

export interface ClinicalResearchCenterDirectoryItemDto {
  readonly name: string;
  readonly legalName: string;
  readonly cnpj: string;
  readonly cnes: string;
  readonly cnesRecords?: readonly CnesRecordDto[];
  readonly cityUf: string;
  readonly address: string;
  readonly bankSummary: string;
  readonly bankIntl?: string;
  readonly bankAccounts?: readonly ResearchCenterBankAccountDto[];
  readonly municipalTaxRegistration?: string;
  readonly stateTaxRegistration?: string;
  readonly technicalDirectorName?: string;
  readonly technicalDirectorCrm?: string;
  readonly legalSignatoryName?: string;
  readonly legalSignatoryCpf?: string;
  readonly legalSignatoryRole?: string;
  readonly legalSignatoryEmail?: string;
  readonly billingContactName?: string;
  readonly billingEmail?: string;
  readonly billingPhone?: string;
  readonly cepName?: string;
  readonly conepNumber?: string;
  readonly fwaNumber?: string;
  readonly classification?: ResearchCenterClassificationType;
  readonly classificationLabel?: string;
  readonly paymentFlowRule?: string;
  readonly giyusRelationship?: string;
  readonly tradeName?: string;
  readonly foundedDate?: string;
  readonly taxIdStatus?: string;
  readonly taxRegime?: string;
  readonly companyPorte?: string;
  readonly legalNature?: string;
  readonly capitalSocial?: string;
  readonly mainCnae?: string;
  readonly secondaryCnaes?: readonly string[];
  readonly allCnaesFormatted?: string;
  readonly qsaSummary?: string;
  readonly qsaMembers?: readonly BigDataCorpQsaMemberDto[];
  readonly neighborhood?: string;
  readonly zipCode?: string;
  readonly phones?: readonly string[];
  readonly emails?: readonly string[];
  readonly isHeadquarter?: boolean;
  readonly branchOrder?: string;
  readonly establishmentTypeLabel?: string;
  readonly enrichedFromBigDataCorp?: boolean;
  readonly lastWeeklySyncAtIso?: string;
  readonly nextWeeklySyncAtIso?: string;
  readonly weeklySyncSchedulePolicy?: string;
}

export interface ClinicalPiDirectoryItemDto {
  readonly name: string;
  readonly crm: string;
  readonly cpf?: string;
  readonly specialty: string;
  readonly secondarySpecialties?: readonly string[];
  readonly email: string;
  readonly commercialEmail?: string;
  readonly personalEmail?: string;
  readonly mobilePhone?: string;
  readonly isPrincipalInvestigator?: boolean;
  readonly isSubInvestigator?: boolean;
  readonly piTherapeuticAreas?: readonly string[];
  readonly subTherapeuticAreas?: readonly string[];
  readonly relatedCenters?: readonly string[];
}

export interface ClinicalPeopleAndCentersDirectoryDto {
  readonly pis: readonly ClinicalPiDirectoryItemDto[];
  readonly subs: readonly StudySubInvestigatorMemberDto[];
  readonly coordinators: readonly StudyCoordinatorMemberDto[];
  readonly researchCenters: readonly ClinicalResearchCenterDirectoryItemDto[];
  readonly specialties?: readonly ClinicalSpecialtyItemDto[];
  readonly healthFunctions?: readonly ClinicalHealthFunctionItemDto[];
  readonly holdingSingletonError?: string | null;
}

export interface RegisterClinicalPersonOrCenterInput {
  readonly kind: 'PI' | 'SUB' | 'COORDINATOR' | 'RESEARCH_CENTER';
  readonly name: string;
  readonly crm?: string;
  readonly cpf?: string;
  readonly specialty?: string;
  readonly secondarySpecialties?: readonly string[];
  readonly role?: string;
  readonly email?: string;
  readonly commercialEmail?: string;
  readonly corporateEmail?: string;
  readonly personalEmail?: string;
  readonly mobilePhone?: string;
  readonly birthDate?: string;
  readonly level?: 'Júnior' | 'Pleno' | 'Sênior';
  readonly isHealthProfessional?: boolean;
  readonly healthProfessionalType?: string;
  readonly councilRegistrationNumber?: string;
  readonly isPrincipalInvestigator?: boolean;
  readonly isSubInvestigator?: boolean;
  readonly piTherapeuticAreas?: readonly string[];
  readonly subTherapeuticAreas?: readonly string[];
  readonly relatedCenters?: readonly string[];
  readonly canActAsSubInvestigator?: boolean;
  readonly legalName?: string;
  readonly cnpj?: string;
  readonly cnes?: string;
  readonly cnesRecords?: readonly CnesRecordDto[];
  readonly cityUf?: string;
  readonly address?: string;
  readonly bankSummary?: string;
  readonly bankIntl?: string;
  readonly bankAccounts?: readonly ResearchCenterBankAccountDto[];
  readonly municipalTaxRegistration?: string;
  readonly stateTaxRegistration?: string;
  readonly technicalDirectorName?: string;
  readonly technicalDirectorCrm?: string;
  readonly legalSignatoryName?: string;
  readonly legalSignatoryCpf?: string;
  readonly legalSignatoryRole?: string;
  readonly legalSignatoryEmail?: string;
  readonly billingContactName?: string;
  readonly billingEmail?: string;
  readonly billingPhone?: string;
  readonly cepName?: string;
  readonly conepNumber?: string;
  readonly fwaNumber?: string;
  readonly classification?: ResearchCenterClassificationType;
  readonly giyusRelationship?: string;
  readonly tradeName?: string;
  readonly foundedDate?: string;
  readonly taxIdStatus?: string;
  readonly taxRegime?: string;
  readonly companyPorte?: string;
  readonly legalNature?: string;
  readonly capitalSocial?: string;
  readonly mainCnae?: string;
  readonly secondaryCnaes?: readonly string[];
  readonly allCnaesFormatted?: string;
  readonly qsaSummary?: string;
  readonly qsaMembers?: readonly BigDataCorpQsaMemberDto[];
  readonly neighborhood?: string;
  readonly zipCode?: string;
  readonly phones?: readonly string[];
  readonly emails?: readonly string[];
  readonly isHeadquarter?: boolean;
  readonly branchOrder?: string;
  readonly establishmentTypeLabel?: string;
  readonly enrichedFromBigDataCorp?: boolean;
  readonly lastWeeklySyncAtIso?: string;
  readonly nextWeeklySyncAtIso?: string;
  readonly weeklySyncSchedulePolicy?: string;
}

export interface WeeklySyncResearchCentersInput {
  readonly forceAll?: boolean;
  readonly targetCnpjs?: readonly string[];
}

export interface WeeklySyncResearchCentersResponseDto {
  readonly ok: boolean;
  readonly syncedCentersCount: number;
  readonly totalCnesSyncedCount: number;
  readonly syncedAtIso: string;
  readonly nextScheduledSyncAtIso: string;
  readonly schedulePolicy: string;
  readonly updatedDirectory: ClinicalPeopleAndCentersDirectoryDto;
}

export interface LookupCnpjBigDataCorpInput {
  readonly cnpj: string;
  readonly includeBranches?: boolean;
  readonly autoRegisterEstablishments?: boolean;
  readonly defaultClassification?: ResearchCenterClassificationType;
  readonly defaultGiyusRelationship?: string;
  readonly defaultCnes?: string;
}

export interface BigDataCorpCnpjLookupResponseDto {
  readonly ok: boolean;
  readonly found: boolean;
  readonly queriedCnpj: string;
  readonly cnpjRoot: string;
  readonly primaryEstablishment: BigDataCorpEstablishmentDto | null;
  readonly headquarterEstablishment: BigDataCorpEstablishmentDto | null;
  readonly branchEstablishments: readonly BigDataCorpEstablishmentDto[];
  readonly allEstablishments: readonly BigDataCorpEstablishmentDto[];
  readonly totalEstablishmentsFound: number;
  readonly autoRegisteredCount: number;
  readonly executionChannel: string;
  readonly updatedDirectory?: ClinicalPeopleAndCentersDirectoryDto;
  readonly error?: string;
}

export interface StudyIncomingDocumentAnalysisItemDto {
  readonly id: string;
  readonly studyCode: string;
  readonly documentKind: 'PROTOCOLO' | 'TCLE' | 'TALE' | 'CONTRATO_CTA' | 'ORCAMENTO_ANEXO';
  readonly documentBadgeLabel: string;
  readonly fileName: string;
  readonly versionOrDateLabel: string;
  readonly arrivalSourceSummary: string;
  readonly extractedObligationsSummary: string;
  readonly crossCheckWithContractAndBudget: string;
  readonly viabilityImpactStatus:
    'CRITICAL_GAP_DETECTED' | 'NEEDS_REDLINE_ADJUSTMENT' | 'ALIGNED_VIABLE';
  readonly estimatedFinancialImpactBrl: number;
  readonly recommendedClauseOrBudgetAdjustment: string;
}

export interface StudyMultiDocSponsorViabilityDto {
  readonly studyCode: string;
  readonly sponsorName: string;
  readonly croName: string;
  readonly documentsAnalyzedCount: number;
  readonly documentKindsPresent: readonly string[];
  readonly viabilityScorePercent: number;
  readonly viabilityVerdict:
    'VIABLE_WITH_REDLINES' | 'HIGH_RISK_REQUIRES_RENEGOTIATION' | 'FULLY_VIABLE_READY';
  readonly viabilityHeadline: string;
  readonly protocolVsBudgetSynthesis: string;
  readonly tcleAndTaleEthicalFinancialSynthesis: string;
  readonly contractVsDurationSynthesis: string;
  readonly actionableCounterproposalBullets: readonly string[];
}

export interface CroSponsorAndGlobalNegotiationLearningDto {
  readonly id: string;
  readonly studyCode: string;
  readonly croName: string;
  readonly croBehaviorPatternLearned: string;
  readonly croConcessionLeversLearned: string;
  readonly sponsorName: string;
  readonly sponsorBudgetPolicyLearned: string;
  readonly sponsorProtocolTcleTaleFlexibilityLearned: string;
  readonly globalContractNegotiationLesson: string;
  readonly obeyedByLlms: readonly string[];
  readonly promotedGlobalRuleId: string;
  readonly updatedAtIso: string;
}

export interface StudyTeamMultiDocAndLearningDashboardDto {
  readonly studyCode: string;
  readonly teamAndCenterBinding: StudyTeamAndCenterBindingDto;
  readonly availablePiCatalog: readonly ClinicalPiDirectoryItemDto[];
  readonly availableSubCatalog: readonly StudySubInvestigatorMemberDto[];
  readonly availableCoordinatorCatalog: readonly StudyCoordinatorMemberDto[];
  readonly availableResearchCentersCatalog: readonly ClinicalResearchCenterDirectoryItemDto[];
  readonly availableSpecialtiesCatalog?: readonly ClinicalSpecialtyItemDto[];
  readonly availableHealthFunctionsCatalog?: readonly ClinicalHealthFunctionItemDto[];
  readonly multiDocumentItems: readonly StudyIncomingDocumentAnalysisItemDto[];
  readonly viabilitySummary: StudyMultiDocSponsorViabilityDto;
  readonly croSponsorGlobalLearnings: readonly CroSponsorAndGlobalNegotiationLearningDto[];
  readonly autoPopulatedDraftPreview: BudgetEmailDraftItemDto;
}

export interface BindStudyTeamAndCenterAutoFillInput {
  readonly studyCode: string;
  readonly piName: string;
  readonly piCrm: string;
  readonly piSpecialty: string;
  readonly piEmail: string;
  readonly subInvestigatorName: string;
  readonly subInvestigatorCrm: string;
  readonly subInvestigatorSpecialty: string;
  readonly subInvestigatorEmail: string;
  readonly subInvestigators?: readonly StudySubInvestigatorMemberDto[];
  readonly coordinatorName: string;
  readonly coordinatorRole: string;
  readonly coordinatorEmail: string;
  readonly coordinators?: readonly StudyCoordinatorMemberDto[];
  readonly researchCenterName: string;
  readonly researchCenterLegalName: string;
  readonly researchCenterCnpj: string;
  readonly researchCenterCnes: string;
  readonly researchCenterCityUf: string;
  readonly researchCenterAddress: string;
  readonly researchCenterBankSummary: string;
  readonly researchCenterClassification?: ResearchCenterClassificationType;
  readonly actorEmail?: string;
}

export interface LearnCroSponsorAndGlobalNegotiationInput {
  readonly studyCode: string;
  readonly croName: string;
  readonly croBehaviorPatternLearned: string;
  readonly croConcessionLeversLearned: string;
  readonly sponsorName: string;
  readonly sponsorBudgetPolicyLearned: string;
  readonly sponsorProtocolTcleTaleFlexibilityLearned: string;
  readonly globalContractNegotiationLesson: string;
  readonly actorEmail?: string;
}

export function useStudyTeamMultiDocAndLearningDashboard(studyCode: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'dossiers', studyCode, 'team-docs-viability'],
    queryFn: () =>
      previewRequest<StudyTeamMultiDocAndLearningDashboardDto>(
        client,
        'get',
        `/api/v1/network/studies/dossiers/${studyCode}/team-docs-viability`,
      ),
    enabled: Boolean(studyCode),
    meta: { silent: true },
  });
}

export function useClinicalPeopleDirectory() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'clinical-people-directory'],
    queryFn: () =>
      previewRequest<ClinicalPeopleAndCentersDirectoryDto>(
        client,
        'get',
        '/api/v1/network/studies/clinical-people-directory',
      ),
    meta: { silent: true },
  });
}

export function useRegisterClinicalPersonOrCenter() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: RegisterClinicalPersonOrCenterInput) =>
      previewRequest<ClinicalPeopleAndCentersDirectoryDto>(
        client,
        'post',
        '/api/v1/network/studies/clinical-people-directory',
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useClinicalSpecialties() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'clinical-specialties'],
    queryFn: () =>
      previewRequest<readonly ClinicalSpecialtyItemDto[]>(
        client,
        'get',
        '/api/v1/network/studies/clinical-specialties',
      ),
    meta: { silent: true },
  });
}

export function useManageClinicalSpecialty() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: ManageClinicalSpecialtyInput) =>
      previewRequest<ManageClinicalSpecialtyResponseDto>(
        client,
        'post',
        '/api/v1/network/studies/clinical-specialties',
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useClinicalHealthFunctions() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['network', 'studies', 'clinical-health-functions'],
    queryFn: () =>
      previewRequest<readonly ClinicalHealthFunctionItemDto[]>(
        client,
        'get',
        '/api/v1/network/studies/clinical-health-functions',
      ),
    meta: { silent: true },
  });
}

export function useManageClinicalHealthFunction() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: ManageClinicalHealthFunctionInput) =>
      previewRequest<ManageClinicalHealthFunctionResponseDto>(
        client,
        'post',
        '/api/v1/network/studies/clinical-health-functions',
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useLookupCnpjBigDataCorp() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: async (input: LookupCnpjBigDataCorpInput): Promise<BigDataCorpCnpjLookupResponseDto> => {
      try {
        const previewRes = await previewRequest<BigDataCorpCnpjLookupResponseDto | undefined>(
          client,
          'post',
          '/api/v1/network/studies/bigdatacorp-cnpj-lookup',
          { body: input },
        );
        if (
          previewRes &&
          (typeof previewRes.found === 'boolean' ||
            Boolean(previewRes.primaryEstablishment || previewRes.headquarterEstablishment))
        ) {
          return previewRes;
        }
      } catch {
        // Fallback para a Azure Static Web Apps Function /api/bigdatacorp-cnpj em produção
      }
      const swaRes = await fetch('/api/bigdatacorp-cnpj', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      const data = (await swaRes.json()) as BigDataCorpCnpjLookupResponseDto & {
        readonly errorMessage?: string;
      };
      return {
        ...data,
        ok: Boolean(data.ok ?? data.found),
        found: Boolean(data.found),
        branchEstablishments: data.branchEstablishments ?? [],
        allEstablishments: data.allEstablishments ?? [],
        totalEstablishmentsFound: data.totalEstablishmentsFound ?? 0,
        autoRegisteredCount: data.autoRegisteredCount ?? 0,
        ...(data.errorMessage && !data.error ? { error: data.errorMessage } : {}),
      };
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useWeeklySyncRegisteredResearchCenters() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: WeeklySyncResearchCentersInput = {}) =>
      previewRequest<WeeklySyncResearchCentersResponseDto>(
        client,
        'post',
        '/api/v1/network/studies/research-centers/weekly-sync',
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useBindStudyTeamAndCenterAutoFill() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: BindStudyTeamAndCenterAutoFillInput) =>
      previewRequest<StudyTeamMultiDocAndLearningDashboardDto>(
        client,
        'post',
        `/api/v1/network/studies/dossiers/${input.studyCode}/team-docs-viability/bind`,
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useLearnCroSponsorAndGlobalNegotiation() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: LearnCroSponsorAndGlobalNegotiationInput) =>
      previewRequest<StudyTeamMultiDocAndLearningDashboardDto>(
        client,
        'post',
        `/api/v1/network/studies/dossiers/${input.studyCode}/team-docs-viability/learn`,
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface ApplyMiroFishProfitMaximizationInput {
  readonly studyCode: string;
  readonly croName: string;
  readonly sponsorName: string;
  readonly monitorProfileLabel: string;
  readonly senderPersonLabel: string;
  readonly studySizeLabel: string;
  readonly sponsorSizeLabel: string;
  readonly studyDurationMonths: number;
  readonly controllablePatientsCount: number;
  readonly controllableRetentionPct: number;
  readonly controllableCounterproposalPct: number;
  readonly controllableScreenFailurePct: number;
  readonly controllablePassThroughShieldPct: number;
  readonly controllableBatchingEfficiencyPct: number;
  readonly controllableAnnualIpcaRebalance: boolean;
  readonly baseNetProfitBrl: number;
  readonly simulatedGrossRevenueBrl: number;
  readonly simulatedNetProfitBrl: number;
  readonly simulatedNetMarginPct: number;
  readonly acceptanceProbabilityPct: number;
  readonly profitGainOverBaseBrl: number;
  readonly tacticalDraftSummary: string;
}

export interface ApplyMiroFishProfitMaximizationResultDto {
  readonly studyCode: string;
  readonly updatedMiroFishSimulationJson: string;
  readonly updatedDraft: BudgetEmailDraftItemDto;
}

export function useApplyMiroFishProfitMaximization() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: (input: ApplyMiroFishProfitMaximizationInput) =>
      previewRequest<ApplyMiroFishProfitMaximizationResultDto>(
        client,
        'post',
        `/api/v1/network/studies/dossiers/${input.studyCode}/mirofish-optimize`,
        { body: input },
      ),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface CanonResyncResultDto {
  readonly canonSchemaVersion: string;
  readonly enforcedRulesCount: number;
  readonly syncedEnginesCount: number;
  readonly upgradedDossiersCount: number;
  readonly upgradedDraftsCount: number;
  readonly harness2State: Harness2EvolutionStateDto;
  readonly globalRules: readonly GlobalAiRuleItemDto[];
}

export function useForceResyncCanonMemory() {
  const client = useApiClient();
  const invalidate = useInvalidateStudiesAndMailboxes();
  return useMutation({
    mutationFn: () =>
      previewRequest<CanonResyncResultDto>(client, 'post', '/api/v1/network/studies/canon-resync', {
        body: {},
      }),
    onSuccess: invalidate,
    meta: { silent: true },
  });
}
